<script setup lang="ts">
import { ref } from 'vue'
import { type Journey } from '~/types/fptfTypes'
import { compressAndBase64Encode } from '~/utils/journeyShare'
import { Share } from '@capacitor/share'
import { Capacitor } from '@capacitor/core'

defineOptions({ inheritAttrs: false })

const { journey } = defineProps<{
  journey: Journey
}>()
const showCopyMessage = ref(false)
const store = useMainStore()

function supportsWebShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
}

async function getShareLink() {
  const config = useRuntimeConfig()

  const journeyJson = JSON.stringify(journey)
  const journeyData = await compressAndBase64Encode(journeyJson)

  try {
    const response = await fetch(config.public.requestBaseUrl + 's/get_share_id/' + journeyData)
    const data = await (await store.displayFetchError(response)).json()
    if (data.id) {
      if (Capacitor.isNativePlatform()) {
        return `https://bahnvorhersage.de/s/s/${data.id}`
      } else {
        return `${window.location.origin}/s/s/${data.id}`
      }
    } else {
      throw new Error('Failed to get share link: ' + data.error)
    }
  } catch (error) {
    console.error('Error fetching share link:', error)
    throw new Error('Failed to get share link: ' + error)
  }
}

async function getAlternativeShareLink() {
  const journeyJson = JSON.stringify(journey)
  const journeyData = await compressAndBase64Encode(journeyJson)
  return `${window.location.origin}/shared#${journeyData}`
}

async function shareJourney() {
  let shareLink: string

  try {
    shareLink = await getShareLink()
  } catch (error) {
    console.error('Error getting share link:', error)
    shareLink = await getAlternativeShareLink()
  }

  const shareData = {
    title: 'Zugverbindung teilen',
    url: shareLink
  }

  // Capacitors Share API fails on web if the browser does not support the Web Share API,
  // so capacitor share is only used on native platforms.
  if (Capacitor.isNativePlatform() && (await Share.canShare())) {
    await Share.share(shareData)
  } else if (supportsWebShare() && navigator.canShare(shareData)) {
    navigator.share(shareData)
  } else {
    navigator.clipboard.writeText(shareLink)

    showCopyMessage.value = true
    setTimeout(() => {
      showCopyMessage.value = false
    }, 3000)
  }
}
</script>

<template>
  <button v-bind="$attrs" class="btn btn-primary flex-fill" @click.stop="shareJourney">
    <i class="icon icon-share-fill"></i> Teilen
  </button>
  <SnackBar @click.stop="" v-if="showCopyMessage">Link kopiert</SnackBar>
</template>
